import React, { useEffect, useRef } from 'react';
import { load } from '@2gis/mapgl';

interface Store {
  id: number | string;
  name: string;
  address?: string;
  lat: number;
  lng: number;
  cluster_id?: number;
  cluster_name?: string;
  cluster_color?: string;
  isClusterCenter?: boolean;
  type?: string;
}

interface Map2GISProps {
  lat?: number;
  lng?: number;
  label?: string;
  stores?: Store[];
  height?: string;
  showClusters?: boolean;
}

const Map2GIS: React.FC<Map2GISProps> = ({ lat, lng, label, stores, height = '400px', showClusters = false }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const apiRef = useRef<any>(null);

  useEffect(() => {
    if (!mapContainer.current) return;
    if (!stores?.length && (!lat || !lng)) return;

    let isMounted = true;

    load().then((mapglAPI) => {
      if (!isMounted || !mapContainer.current) return;
      apiRef.current = mapglAPI;
      
      // Determine center and zoom
      let center: [number, number];
      let zoom = 13;
      
      if (stores?.length) {
        // Calculate bounds from all stores
        const lats = stores.map(s => s.lat);
        const lngs = stores.map(s => s.lng);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        center = [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
        
        // Adjust zoom based on bounds
        const latDiff = maxLat - minLat;
        const lngDiff = maxLng - minLng;
        const maxDiff = Math.max(latDiff, lngDiff);
        if (maxDiff > 0.5) zoom = 10;
        else if (maxDiff > 0.2) zoom = 11;
        else if (maxDiff > 0.1) zoom = 12;
      } else {
        center = [lng!, lat!];
      }
      
      if (!mapRef.current) {
        mapRef.current = new mapglAPI.Map(mapContainer.current, {
          center,
          zoom,
          key: 'c95beca9-8052-4aa9-a9c0-367a67ca8fe4',
        });
      } else {
        mapRef.current.setCenter(center);
        mapRef.current.setZoom(zoom);
      }
      
      // Clear existing markers
      markersRef.current.forEach(marker => marker.destroy());
      markersRef.current = [];
      markerRef.current?.destroy();
      markerRef.current = null;
      
      // Create markers for stores
      if (stores?.length) {
        stores.forEach(store => {
          const markerOptions: any = {
            coordinates: [store.lng, store.lat],
          };
          
          // Customize marker based on type
          if (store.isClusterCenter) {
            markerOptions.icon = 'https://docs.2gis.com/img/mapgl/pin-red.svg';
            markerOptions.size = [48, 48];
            markerOptions.label = {
              text: store.name,
              offset: [0, -24],
              fontSize: 14,
              fontWeight: 'bold'
            };
          } else {
            markerOptions.label = {
              text: store.name,
              offset: [0, -20],
              fontSize: 12
            };
            if (showClusters && store.cluster_color) {
              // Use cluster color for marker - 2GIS doesn't support custom colors directly
              // so we'll use different icons or sizes
              markerOptions.size = [32, 32];
            }
          }
          
          const marker = new mapglAPI.Marker(mapRef.current, markerOptions);
          markersRef.current.push(marker);
        });
      } else if (lat && lng) {
        // Single location marker
        markerRef.current = new mapglAPI.Marker(mapRef.current, {
          coordinates: [lng, lat],
          label: {
            text: label || 'Location',
            offset: [0, -40],
          },
        });
      }
    });

    return () => {
      isMounted = false;
      markersRef.current.forEach(marker => marker.destroy());
      markersRef.current = [];
      markerRef.current?.destroy();
      markerRef.current = null;
    };
  }, [lat, lng, label, stores, showClusters]);

  return <div ref={mapContainer} style={{ width: '100%', height }} />;
};

export default Map2GIS;